import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '../store/auth.store.js';
import { BackgroundEffects } from '../components/lobby/BackgroundEffects.js';
import { OnlineStatus } from '../components/lobby/OnlineStatus.js';
import { CreateRoomPanel } from '../components/room-hub/CreateRoomPanel.js';
import type { CreateRoomConfig } from '../components/room-hub/CreateRoomPanel.js';
import { JoinRoomPanel } from '../components/room-hub/JoinRoomPanel.js';
import { PublicRoomsPanel } from '../components/room-hub/PublicRoomsPanel.js';
import { useRoomStore } from '../store/room.store.js';
import { colyseusClient } from '../api/colyseus.js';

export function RoomHubScreen() {
  const { user, isSessionChecked } = useAuthStore();
  const { createRoom, joinRoom } = useRoomStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Route protection
  useEffect(() => {
    if (isSessionChecked && !user) {
      navigate('/login');
    }
  }, [user, isSessionChecked, navigate]);

  const handleBack = () => {
    navigate('/home');
  };

  const handleCreateRoom = async (config: CreateRoomConfig) => {
    if (user) {
      await createRoom(config, user.displayName);
      navigate('/waiting-room');
    }
  };

  const handleJoinRoom = async (code: string) => {
    if (user) {
      await joinRoom(code, user.displayName);
      navigate('/waiting-room');
    }
  };

  const handleRefresh = () => {
    alert('Room list refreshed!');
  };

  const handleQuickPlay = async () => {
    if (user) {
      try {
        const availableRooms = await colyseusClient.getAvailableRooms('game');
        if (availableRooms.length > 0) {
          const roomToJoin = availableRooms[0];
          if (roomToJoin) {
            await joinRoom(roomToJoin.roomId, user.displayName);
            navigate('/waiting-room');
            return;
          }
        }
      } catch (err) {
        console.error('Quick play room query error:', err);
      }

      // Fallback
      await createRoom({
        name: `${user.displayName}'s Arena`,
        maxPlayers: 30,
        gameSpeed: 'normal',
        callerMode: 'auto',
        prizePreset: 'standard',
        allowLateMarking: true,
      }, user.displayName);
      navigate('/waiting-room');
    }
  };

  // Stagger configurations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
  };

  return (
    <div className="relative flex h-screen w-screen flex-col justify-between overflow-hidden select-none">
      {/* Lobby background effects */}
      <BackgroundEffects />

      {/* Hub Custom Header */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative w-full flex items-center justify-between px-4 py-3 backdrop-blur-md border-b border-white/5 bg-slate-950/20 z-30 h-14"
      >
        {/* Back navigation button */}
        <button
          onClick={handleBack}
          className="flex items-center justify-center w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-surface-200 hover:text-white transition-colors cursor-pointer z-10"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        {/* Title */}
        <div className="absolute left-1/2 transform -translate-x-1/2 flex flex-col items-center text-center pointer-events-none whitespace-nowrap">
          <h2 className="font-display text-sm font-black tracking-wider text-white uppercase">
            Room Hub
          </h2>
        </div>

        {/* Connection & player profile */}
        <div className="flex items-center z-10">
          {user && (
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-purple-600 to-pink-500 flex items-center justify-center text-xs font-black shadow-glow">
              {user.displayName.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
      </motion.header>

      {/* Main 3 columns layout */}
      <main className="flex-1 flex flex-col justify-center items-center px-6 py-4 max-h-[calc(100vh-140px)] z-20 overflow-y-auto w-full">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="w-full flex justify-center items-center h-full"
        >
          {pathname === '/create-room' && (
            <motion.div variants={itemVariants} className="w-full max-w-md h-full max-h-[460px]">
              <CreateRoomPanel onCreateRoom={handleCreateRoom} />
            </motion.div>
          )}

          {pathname === '/join-room' && (
            <motion.div variants={itemVariants} className="w-full max-w-md h-full max-h-[320px]">
              <JoinRoomPanel onJoinRoom={handleJoinRoom} />
            </motion.div>
          )}

          {pathname === '/rooms' && (
            <motion.div variants={itemVariants} className="w-full max-w-3xl h-full max-h-[460px]">
              <PublicRoomsPanel onJoinRoom={handleJoinRoom} />
            </motion.div>
          )}
        </motion.div>
      </main>

      {/* Bottom Action Bar */}
      <motion.footer
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="w-full flex items-center justify-between px-6 py-4 border-t border-white/5 bg-slate-950/20 z-30"
      >
        <div className="flex items-center space-x-3">
          {/* Quick Play shortcut
          <button
            onClick={handleQuickPlay}
            className="flex items-center space-x-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white text-xs font-black uppercase tracking-widest cursor-pointer shadow-glow"
          >
            <span>⚡</span>
            <span>Quick Play</span>
          </button>
          */}
          
          {/* Refresh shortcut */}
          <button
            onClick={handleRefresh}
            className="flex items-center space-x-1.5 px-3 py-2 rounded-full bg-slate-900/40 hover:bg-slate-900/70 border border-white/5 text-surface-200 hover:text-white text-xs font-bold uppercase tracking-wider cursor-pointer"
          >
            <span>🔄</span>
            <span>Refresh Rooms</span>
          </button>
        </div>

        <p className="text-[10px] font-bold tracking-widest font-mono text-surface-500 uppercase">
          CONNECTED &bull; HUB V1.0
        </p>
      </motion.footer>
    </div>
  );
}
